// src/firebase.js
import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { getAnalytics, logEvent as firebaseLogEvent, isSupported as analyticsIsSupported } from 'firebase/analytics';
import logger from './utils/logger';

// Firebase configuration
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

// Initialize Firebase
let app;
let auth;
let db;
let storage;
let messaging = null;
let analytics = null;
let isInitialized = false;
let initializationPromise = null;

const initializeFirebase = async () => {
  // Skip Firebase initialization in test environment
  if (process.env.NODE_ENV === 'test' || process.env.JEST_WORKER_ID !== undefined) {
    isInitialized = true;
    return { app: {}, auth: {}, db: {}, storage: {}, messaging: null };
  }

  if (isInitialized) {
    return { app, auth, db, storage, messaging };
  }

  if (initializationPromise) {
    return initializationPromise;
  }

  initializationPromise = (async () => {
    try {
      app = initializeApp(firebaseConfig);
      auth = getAuth(app);
      db = getFirestore(app);
      storage = getStorage(app);

      // Initialize messaging only if supported
      if (typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window) {
        try {
          messaging = getMessaging(app);
        } catch (error) {
          logger.warn('Firebase messaging not supported:', error);
        }
      }

      // Initialize Analytics (GA4) — only in browser, skip in emulator mode
      if (typeof window !== 'undefined' && process.env.REACT_APP_USE_EMULATORS !== 'true' && firebaseConfig.measurementId) {
        try {
          const supported = await analyticsIsSupported();
          if (supported) {
            analytics = getAnalytics(app);
          }
        } catch (error) {
          logger.warn('Firebase Analytics not supported:', error);
        }
      }

      // Connect to Firebase Emulators when REACT_APP_USE_EMULATORS is set
      if (process.env.REACT_APP_USE_EMULATORS === 'true' && typeof window !== 'undefined') {
        const emulatorHost = 'localhost';
        connectAuthEmulator(auth, `http://${emulatorHost}:9099`, { disableWarnings: true });
        connectFirestoreEmulator(db, emulatorHost, 8080);
        connectStorageEmulator(storage, emulatorHost, 9199);
        logger.info('Connected to Firebase Emulators');
      }

      // Expose Firebase instances globally for debugging (development only)
      if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
        window.Firebase = {
          app,
          auth,
          db,
          storage,
          messaging,
          analytics
        };
      }

      isInitialized = true;
      return { app, auth, db, storage, messaging };
    } catch (error) {
      logger.error('Error initializing Firebase:', error);
      throw error;
    }
  })();

  return initializationPromise;
};

export const ensureFirebaseInitialized = () => {
  return initializeFirebase();
};

// Initialize Firebase immediately (skip in test environment)
const firebasePromise = initializeFirebase();

// Google Sign-In Provider (skip in test environment)
const googleProvider = process.env.NODE_ENV === 'test' || process.env.JEST_WORKER_ID !== undefined
  ? {}
  : new GoogleAuthProvider();

const signInWithGoogle = async () => {
  await ensureFirebaseInitialized();

  try {
    const result = await signInWithPopup(auth, googleProvider);
    const user = result.user;

    // Do NOT create a Firestore user document here.
    // The AccountCreationWizard handles full profile creation (including
    // familyCode, role, groups, etc.).  Creating a minimal stub here caused
    // race conditions where AuthContext read the stub before the wizard
    // could run, letting users slip past the signup guard.

    return user;
  } catch (error) {
    logger.error('Error during Google Sign-In:', error);
    throw error;
  }
};

// Helper function to get collection path based on view type
const getCollectionPath = (collectionType, isPersonal = true, familyCode = null) => {
  if (!familyCode && auth.currentUser?.familyCode) {
    familyCode = auth.currentUser.familyCode;
  }
  
  if (isPersonal) {
    return `users/${auth.currentUser?.uid}/${collectionType}`;
  } else {
    return `families/${familyCode}/${collectionType}`;
  }
};

// Helper function to get storage path based on view type
const getStoragePath = (collectionType, isPersonal = true, familyCode = null) => {
  if (!familyCode && auth.currentUser?.familyCode) {
    familyCode = auth.currentUser.familyCode;
  }
  
  if (isPersonal) {
    return `users/${auth.currentUser?.uid}/${collectionType}`;
  } else {
    return `families/${familyCode}/${collectionType}`;
  }
};

/**
 * Log a GA4 event. No-ops silently when analytics isn't initialized
 * (local dev without measurementId, or unsupported browser).
 *
 * Usage: logAnalyticsEvent('login', { method: 'google' });
 */
export const logAnalyticsEvent = (eventName, params) => {
  if (analytics) {
    firebaseLogEvent(analytics, eventName, params);
  }
};

export {
  app,
  auth,
  db,
  storage,
  messaging,
  analytics,
  getToken,
  onMessage,
  signInWithGoogle,
  getCollectionPath,
  getStoragePath,
  firebasePromise
};